/**
 * Attendre que le son soit là — sans y rester.
 *
 * # Pourquoi pas `await context.resume()`
 *
 * C'est ce qu'on faisait, et ça gelait l'application entière. WebKit
 * n'accomplit pas la promesse de `resume()` : il l'accroche à l'état
 * `running`, et un contexte que l'arrière-plan a interrompu peut ne plus
 * jamais l'atteindre. L'entrée dans le studio restait alors sur cette ligne
 * pour de bon — pas de jingle, pas de rouleau, rien dans la console.
 *
 * # Pourquoi attendre quand même
 *
 * Ne plus attendre du tout a son propre défaut, et il s'est vu aussitôt : le
 * jingle défilait en silence. Rien de ce qui se programme avant que le
 * contexte rende ne sonne — `ScheduleTicker` ne diffuse aucune fenêtre tant
 * qu'il ne rend pas — et sur iPhone le réveil demande parfois plus que les
 * quelques millisecondes qu'il coûte ailleurs.
 *
 * Alors on attend l'état, pas la promesse, et on se donne une limite. Le
 * contexte sain rend la main en une image ; celui qui ne repart pas laisse
 * passer la suite après le délai, muette mais vivante. Jamais d'écran figé.
 */

/** Ce que cette fonction demande d'un `AudioContext`, et rien de plus. */
export type RunnableAudioContext = Pick<
  AudioContext,
  "state" | "addEventListener" | "removeEventListener"
>;

/**
 * Rend `true` dès que `context` tourne, `false` si le délai passe d'abord.
 *
 * Rend la main tout de suite sur un contexte déjà `running`, et tout de suite
 * aussi sur un contexte `closed` — il n'y a plus rien à attendre.
 */
export function whenAudioRunning(
  context: RunnableAudioContext,
  timeoutMs: number,
): Promise<boolean> {
  if (context.state === "running") {
    return Promise.resolve(true);
  }

  if (context.state === "closed") {
    return Promise.resolve(false);
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;

    const done = (running: boolean) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timer);
      context.removeEventListener("statechange", onStateChange);
      resolve(running);
    };

    function onStateChange(): void {
      if (context.state === "running") {
        done(true);

        return;
      }

      if (context.state === "closed") {
        done(false);
      }
    }

    const timer = setTimeout(() => done(false), timeoutMs);

    context.addEventListener("statechange", onStateChange);
  });
}
